import { ConcurrencyQueue } from "@root/utils/concurrencyQueue";
import type { OfferKind } from "@root/utils/enums/offer";
import { useCallback, useEffect, useRef, useState } from "react";
import { type ILlmExtractJobOffer, postExtractOffer, postParseOffer } from ".";

export type TGeneratedOfferItem<TRaw> =
  | { status: "pending" }
  | { status: "fulfilled"; value: TRaw }
  | { status: "rejected" };

const MAX_CONCURRENT_EXTRACTIONS = 3;

const extractOffer = async <TRaw>(
  offerText: string,
  kind: OfferKind,
  signal: AbortSignal
): Promise<TGeneratedOfferItem<TRaw>> => {
  try {
    const res = await postExtractOffer<TRaw>({ offerText, kind }, signal);
    return { status: "fulfilled", value: res.data };
  } catch {
    return { status: "rejected" };
  }
};

export const useGenerateOffersFromIA = <TRaw = ILlmExtractJobOffer>(kind: OfferKind) => {
  const [generatedOffers, setGeneratedOffers] = useState<TGeneratedOfferItem<TRaw>[]>([]);
  const abortRef = useRef<AbortController>();
  // One queue for the whole hook instance, so the concurrency cap holds even when
  // a new run starts while the previous one is still draining.
  const queueRef = useRef<ConcurrencyQueue>();
  if (!queueRef.current) {
    queueRef.current = new ConcurrencyQueue(MAX_CONCURRENT_EXTRACTIONS);
  }

  const generate = useCallback(
    async (text: string): Promise<number> => {
      // Abort the run still in flight: its parse/extract requests reject and its
      // queued extractions get skipped, so a stale run can neither overwrite state
      // nor keep hitting the backend.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      let offerTexts: string[];
      try {
        const parseRes = await postParseOffer({ text, kind }, controller.signal);
        offerTexts = parseRes.data.offerTexts;
      } catch (error) {
        // A newer run (or a cancel) aborted this parse: expected, stay silent.
        if (controller.signal.aborted) {
          return 0;
        }
        // Genuine parse failure: let the caller surface it to the user.
        throw error;
      }

      setGeneratedOffers(offerTexts.map(() => ({ status: "pending" })));

      for (const [index, offerText] of offerTexts.entries()) {
        queueRef.current?.add(async () => {
          const item = await extractOffer<TRaw>(offerText, kind, controller.signal);
          // The queue skips tasks aborted before they start; this guards the ones
          // aborted while their request was in flight.
          if (controller.signal.aborted) {
            return;
          }
          setGeneratedOffers((prev) => prev.map((current, i) => (i === index ? item : current)));
        }, controller.signal);
      }

      return offerTexts.length;
    },
    [kind]
  );

  const cancelGeneration = useCallback(() => {
    abortRef.current?.abort();
    setGeneratedOffers([]);
  }, []);

  // Stop any in-flight generation when the consumer unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  return { generatedOffers, generate, cancelGeneration };
};
